/**
 * Drawing the fire — crossed planes, an animated texture, and no shading.
 *
 * **T108 slice 6 · `Development/Minecraft/15_Animated_Textures_And_The_Flame.md` §4**
 *
 * ## 🔴 The shape is a CROSS, and that is read off Minecraft's own model
 *
 * `template_campfire.json` has six elements. Four are logs and carry the
 * collision box. **The other two are the fire: two flat quads at right angles,
 * rotated 45°, `shade: false`, sixteen pixels tall.**
 *
 * ⚠️ **Doc 13 said the opposite** — that the campfire *"is not a billboard"* and
 * therefore had no ready-made cross answer. It was half right: the **block** has
 * a real collision box, and the **flame standing on it** is a cross. 👉 **So
 * T159's cross work and the flame are one job, not two.**
 *
 * ## Why this is a separate object from the world mesh
 *
 * The world is **one merged mesh with one opaque material**, greedy-culled. A
 * flame is transparent, unshaded, animated, and not a cube. **Every one of those
 * is a reason it cannot ride in that mesh**, so it gets its own geometry and its
 * own material, positioned per chunk exactly as the terrain is.
 *
 * ## 🔴 `shade: false` is not a detail
 *
 * Both of Minecraft's fire elements refuse directional shading. **A flame lit by
 * our sun would go dim on its north side**, which is the one thing a fire must
 * never do. ✅ Doc 13 already found this for flowers and called it *"what stops
 * a flower looking like a folded card"*. Here it means a `MeshBasicNodeMaterial`
 * — lighting is not applied at all — rather than the Lambert material the world
 * uses.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  DataArrayTexture,
  DoubleSide,
  LinearFilter,
  Mesh,
  NearestFilter,
  SRGBColorSpace,
} from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { attribute, float, texture, uniform, uv, vec3 } from 'three/tsl';
import { FLAME_FRAMES, FLAME_RGBA, FLAME_SECOND_SHEET_OFFSET, FLAME_SIZE } from '@lofi/shared';

/**
 * How tall the flame stands, in metres.
 *
 * **Minecraft's is 16 px of a 16 px block from y = 1 to y = 17** — a full block
 * tall, starting one pixel up, so it **overhangs the top of its own cell**. That
 * overhang is copied: a flame that stops exactly at the block's ceiling looks
 * cut off.
 */
export const HEARTH_FLAME_HEIGHT = 1.0;

/**
 * 🔴 **How tall the hearth's SOLID part is, and this is the number that was
 * wrong.**
 *
 * 🎯 **haugur, 2026-08-27, first time he saw it:** *"i just bearly see the tiny
 * top of the flames, just tiny bit poking out from the top, is like the flames
 * start at the bottom of the campfireblock instead of on top of it."* **They
 * did.**
 *
 * ⚠️ **Minecraft's numbers were copied without Minecraft's SHAPE.** Its
 * campfire is four log elements only **7/16 of a block tall**, so a flame
 * starting at y = 1/16 stands mostly in open air above them. **Ours is still a
 * full cube** (T160 has not been built), so the same offset buried the whole
 * flame inside solid rock and left one pixel showing.
 *
 * ✅ **T160 BUILT, 2026-08-27, AND THIS CAME DOWN WITH IT.** The hearth is now
 * a quarter — haugur's own plan, chosen over Minecraft's seven sixteenths after
 * seeing both drawn to scale.
 *
 * 🔴 **THIS NUMBER AND `HEIGHTS["eldstaedi"]` IN `tools/build-palette.py` ARE
 * THE SAME MEASUREMENT IN TWO PLACES, AND NOTHING MAKES THEM AGREE.** If they
 * drift the flame either floats above the stones or sits buried inside them —
 * and it was buried once already, with one pixel showing. **A test pins them
 * together; that test is the only thing holding this joint.**
 */
export const HEARTH_SOLID_TOP = 0.25;

/**
 * How far the flame roots INTO the block it stands on.
 *
 * A flame whose bottom edge sits exactly on the top face reads as balanced on a
 * shelf. Sinking it slightly means the base is hidden by the block's own depth
 * — the material still depth-TESTS, it just never depth-writes — so the fire
 * looks like it is coming out of the hearth rather than resting on it.
 */
const FLAME_SINK = 1 / 16;

/** Where the flame starts, in metres above the block's floor. */
export const HEARTH_FLAME_BASE = HEARTH_SOLID_TOP - FLAME_SINK;

/** How wide each plane is. Minecraft's is 14.4 px of 16, inset from the edges. */
export const HEARTH_FLAME_WIDTH = 0.9;

/**
 * The frames, uploaded once as a texture array — **one layer per frame.**
 *
 * ✅ **The same machinery the block tiles already use**, rather than a second way
 * of doing textures. The shader picks a layer; nothing is re-uploaded per frame.
 */
export function buildFlameTexture(): DataArrayTexture {
  const tex = new DataArrayTexture(FLAME_RGBA, FLAME_SIZE, FLAME_SIZE, FLAME_FRAMES);
  tex.colorSpace = SRGBColorSpace;
  /* Nearest when magnified, so the frames stay crisp pixels rather than being
     smeared — the same call the block tiles make, and the look haugur's eye is
     calibrated to. ⚠️ No mipmaps: a flame is never seen small enough to shimmer,
     and a mip chain would blend frames of an animation into each other. */
  tex.magFilter = NearestFilter;
  tex.minFilter = LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/** The frame the whole world is showing, driven from the render loop. */
export const flameFrame = uniform(0);

/**
 * The material every flame shares.
 *
 * 🔴 **`MeshBasicNodeMaterial`, so the sun never touches it.** That is
 * `shade: false`.
 *
 * ⚠️ **Additive blending, which is a choice worth naming.** A flame is light
 * rather than a surface: adding it to what is behind means it brightens the
 * ground and the logs instead of sitting on them like a sticker, and two
 * overlapping planes read as *hotter* where they cross rather than as a seam.
 * 👉 **It is also the nearest thing to light emission we have until light is
 * built** — the flame will not light the room, but it will not look painted on
 * either.
 */
export function buildFlameMaterial(tex: DataArrayTexture): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial();

  /* 🔴 Two sheets, out of phase. The second plane carries a per-vertex offset
     of half the loop, so the two never agree and the flame stops reading as a
     repeat. Doc 15 §2 — one line of data in Minecraft, one attribute here. */
  const phase = attribute('phase', 'float');
  const layer = flameFrame.add(phase).mod(float(FLAME_FRAMES)).round().toInt();
  const tile = texture(tex, uv()).depth(layer);

  material.colorNode = vec3(tile.r, tile.g, tile.b);
  material.opacityNode = tile.a;
  material.transparent = true;
  material.blending = AdditiveBlending;
  /* 🔴 Never writes depth. Two crossed planes both transparent would otherwise
     hide each other depending on which was drawn first, and the flame would
     flicker as the camera turned. */
  material.depthWrite = false;
  material.side = DoubleSide;
  /* Anything this faint is not fire, and dropping it early keeps the fill rate
     down where a hundred hearths are in view. */
  material.alphaTest = 0.02;

  return material;
}

/**
 * Build the crossed planes for every fire in one chunk.
 *
 * @param positions chunk-local block positions, as x,y,z triples
 * @returns `null` when there is nothing burning, so the caller adds no object
 *
 * ⚠️ **One mesh for the whole chunk, not one per fire.** A hearth is a small
 * thing and a farm may have several; a draw call each would be wasteful for
 * eight vertices.
 */
/* ⚠️ No eslint-disable here any more. This wrapper stopped touching typed
   arrays the day the crossed-plane loop moved into `buildFlames` below, which
   carries the suppression — and an unused directive is itself a lint error, so
   the rule keeps its own scope honest. */
export function buildFlameGeometry(positions: readonly number[]): BufferGeometry | null {
  return buildFlames(
    /* The hearth's own numbers. A block position names a CELL, so the flame is
       centred in it and starts at the top of the stones. */
    Array.from({ length: positions.length / 3 }, (_unused, i) => ({
      cx: (positions[i * 3] ?? 0) + 0.5,
      cy: (positions[i * 3 + 1] ?? 0) + HEARTH_FLAME_BASE,
      cz: (positions[i * 3 + 2] ?? 0) + 0.5,
      height: HEARTH_FLAME_HEIGHT,
      width: HEARTH_FLAME_WIDTH,
    })),
  );
}

/**
 * 🔥 **One fire, anywhere, at any size.**
 *
 * 🔴 **This is the hearth's own crossed-plane code, generalised rather than
 * copied**, because the torch needed the identical thing a third of the size and
 * half a metre up in the air. ⚠️ **A second copy of it would have been a
 * second flame to keep in step with the first** — and the whole point of the
 * torch reusing this texture is that *the torch in your hand, the torch on the
 * wall and the fire in your hearth are one fire.*
 *
 * `cx` and `cz` are the flame's CENTRE and `cy` is its BASE, all in the same
 * chunk-local metres the mesh uses.
 */
export interface FlameSpec {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
  readonly height: number;
  readonly width: number;
}

/* eslint-disable security/detect-object-injection --
   Same reasoning as the wrapper above: loop counters over typed arrays this
   function has just allocated. */
export function buildFlames(flames: readonly FlameSpec[]): BufferGeometry | null {
  if (flames.length === 0) return null;

  const count = flames.length;
  /* Two planes, four corners each. */
  const verts = count * 8;
  const pos = new Float32Array(verts * 3);
  const uvs = new Float32Array(verts * 2);
  const phase = new Float32Array(verts);
  const indices = new Uint32Array(count * 12);

  for (let i = 0; i < count; i++) {
    const f = flames[i];
    if (f === undefined) continue;

    /* Half-width along each diagonal. The planes are at 45° to the block's own
       axes — Minecraft's `rotation: { angle: 45, axis: y }` — so from a corner
       you see the flat of one plane rather than the edge of both. */
    const h = (f.width / 2) * Math.SQRT1_2;

    const bx = f.cx;
    const by = f.cy;
    const bz = f.cz;
    const bottom = 0;
    const top = f.height;

    /* Plane A runs from (-h,-h) to (+h,+h); plane B the other diagonal. */
    const planes = [
      { x0: -h, z0: -h, x1: h, z1: h, phase: 0 },
      { x0: -h, z0: h, x1: h, z1: -h, phase: FLAME_SECOND_SHEET_OFFSET },
    ];

    for (const [p, plane] of planes.entries()) {
      const base = (i * 2 + p) * 4;
      const corners = [
        [plane.x0, bottom, plane.z0, 0, 1],
        [plane.x1, bottom, plane.z1, 1, 1],
        [plane.x1, top, plane.z1, 1, 0],
        [plane.x0, top, plane.z0, 0, 0],
      ];

      for (const [c, corner] of corners.entries()) {
        const v = base + c;
        pos[v * 3] = bx + (corner[0] ?? 0);
        pos[v * 3 + 1] = by + (corner[1] ?? 0);
        pos[v * 3 + 2] = bz + (corner[2] ?? 0);
        uvs[v * 2] = corner[3] ?? 0;
        uvs[v * 2 + 1] = corner[4] ?? 0;
        phase[v] = plane.phase;
      }

      const q = (i * 2 + p) * 6;
      indices[q] = base;
      indices[q + 1] = base + 1;
      indices[q + 2] = base + 2;
      indices[q + 3] = base;
      indices[q + 4] = base + 2;
      indices[q + 5] = base + 3;
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(pos, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setAttribute('phase', new BufferAttribute(phase, 1));
  geometry.setIndex(new BufferAttribute(indices, 1));
  return geometry;
}
/* eslint-enable security/detect-object-injection */

/** A chunk's fires as one object, or `null` if nothing there is burning. */
export function buildFlameMesh(
  positions: readonly number[],
  material: MeshBasicNodeMaterial,
): Mesh | null {
  const geometry = buildFlameGeometry(positions);
  if (geometry === null) return null;
  const mesh = new Mesh(geometry, material);
  /* Drawn after the world, because it is transparent and adds to what is
     behind it. */
  mesh.renderOrder = 1;
  return mesh;
}
